import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ProductGridRelatedProductsContext from '~/grid/context/ProductGridRelatedProductsContext';
import useInitialProductPricing from '~/grid/utils/useInitialProductPricing';
import useInitialProductRelatedProducts from '~/grid/utils/useInitialProductRelatedProducts';
import { useReportAlgorithmTrace } from '~/grid/utils/useReportAlgorithmTrace';
import CloseIcon from '~/icons/components/CloseIcon';
import CarouselNavigation from '~/shared/components/CarouselNavigation';
import LoadingSpinnerIcon from '~/shared/components/LoadingSpinner';
import TypedText from '~/shared/components/TypedText';
import featureNames from '~/shared/constants/featureFlags';
import {
  overlayColor,
  imageAspectRatio,
  pageSize,
} from '~/shared/constants/productGrid';
import { cookieSizeMap } from '~/shared/constants/productSizes';
import { useDefaultProductFilters } from '~/shared/useDefaultProductFilters';
import { getFilterSettings } from '~/shared/utils/getFilterSettings';
import { modifyFiltersForLastChance } from '~/shared/utils/modifyFiltersForLastChance';
import { getExpandedPlusSizeFilters } from '~/shared/utils/plusSizeFilters.js';
import { useCioClient } from '~/shared/utils/useCioClient';
import useGender from '~/shared/utils/useGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useCategoryLabels } from '~/shared/utils/useLabels';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useAdminTools } from '~/techstyle-shared/react-admin';
import { useAssets } from '~/techstyle-shared/react-assets';
import {
  CategoryLabelProvider,
  EmptyGridMessage,
  Button,
  FilterDrawerCustomFilters as BaseFilterDrawer,
  FilterDrawerButton as BaseFilterDrawerButton,
  FloatingFilterButton as BaseFloatingFilterButton,
  GridProvider,
  HeaderAttachment as BaseHeaderAttachment,
  LoadingStatus,
  OptionLabelProvider,
  ProductBrowserCustomFilters as BaseProductBrowser,
  ProductBrowserSideBar,
  ProductListingFilterStyle,
  ProductSizeFilter as BaseProductSizeFilter,
  mobile,
  desktop,
  useBreakpoint,
  useGridState,
  useLoadingStatus,
  useProductBrowser,
  ProductGridPricingContext,
  useOptionLabels,
} from '~/techstyle-shared/react-components';
import { useApolloClient } from '~/techstyle-shared/react-graphql';
import { FormattedMessage, useRegion } from '~/techstyle-shared/react-intl';
import {
  ProductListViewedProvider,
  useProductActions,
  useProductListingsReducer,
} from '~/techstyle-shared/react-products';
import { VirtualScroll } from '~/techstyle-shared/react-virtual-scroll';
import { useCookies, useDomain } from '~/techstyle-shared/redux-core';

import { BreakpointsShape } from '../../constants/customPropTypes';
import FloatingFilterBarContext from '../../context/FloatingFilterBarContext';
import { useGridScrollContext } from '../../context/GridScrollContext';
import addNewGridColumnSettings, {
  gridColumnsSettingsType,
} from '../../utils/addNewGridColumnSettings';
import getSizeCookies from '../../utils/getSizeCookies';
import { getColorSwatches } from '../../utils/productFilters.js';
import useProductImageType from '../../utils/useProductImageType';
import ColorWheelFilter from '../ColorWheelFilter';
import DesktopProductListingFilter from '../DesktopProductListingFilter';
import FilterTags from '../FilterTags';
import FloatingFilterBar from '../FloatingFilterBar/FloatingFilterBar';
import GridFilterSegmentEvents from '../GridFilterSegmentEvents/GridFilterSegmentEvents';
import MobileProductListingFilter from '../MobileProductListingFilter';
import { useProductBrowserFilterState } from '../ProductBrowserFilterContext';
import BaseProductGrid from '../ProductGrid';
import BaseSortDropdown from '../SortDropdown';

import {
  filterFutureProducts,
  getCioBrowseProducts,
  getCioSearchProducts,
  getGqlProducts,
  getV2Products,
} from './utils';

const DynamicFormattedMessage = FormattedMessage;

const LoadingStatusContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  /* Left position will need to change if width of filters sidebar change
 in order to keep it centered on desktop */
  left: calc(50% + 150px);
  transform: translateX(-50%);
  right: 0;
  z-index: 3;

  ${mobile`
		left: 50%;
  `}
`;

const LoadingIconWrapper = styled.div`
  position: sticky;
  line-height: 0.7;
  top: 50%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 44px;
`;

const ResultsCountWrapper = styled(TypedText).attrs({ size: 16 })`
  padding-top: 16px;
  min-height: 36px;
`;

const GridContent = styled.div`
  flex: 1 1 auto;
`;

const MobileFiltersOnlySortPlaceholder = styled.div``;

const FloatingFilterButton = styled(BaseFloatingFilterButton)`
  border-radius: 23px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  height: 34px;
  min-width: 88px;
  z-index: 0;
  position: absolute;
  bottom: 0;
  transform: translateY(50px);

  ${mobile`
    order: 2;
  `}

  ${({ theme }) => theme.floatingFilterButton};
`;

const filterDrawerDialogStyle = css`
  box-shadow:
    -4px 0 8px 0 rgba(0, 0, 0, 0.04),
    inset 1px 0 0 0 ${({ theme }) => theme.colors.palette.white};
`;

const noFiltersAppliedStyle = css`
  font-size: 16px;
  letter-spacing: normal;
  margin-bottom: 16px;

  ${({ theme }) => theme.gridFilterStyles.noFiltersAppliedStyle}
`;

const applyFiltersButtonStyle = css`
  &&& {
    letter-spacing: normal;
    color: ${({ theme }) => theme.colors.textLight};
    opacity: ${({ disabled }) => (disabled ? 0.25 : 1)};

    ${({ theme }) => theme.gridFilterStyles.applyFiltersButtonStyle}
  }
`;

const filterTagWrapperStyle = css`
  position: unset;
  padding: 16px 24px;
`;

const FilterDrawerButton = styled(BaseFilterDrawerButton)`
  font-size: 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.fontStacks.textFamily};
  color: ${({ theme }) => theme.colors.textDefault};

  ${({ theme }) => theme.gridStyles.filterDrawerButton};
`;

const HeaderAttachment = styled(BaseHeaderAttachment)``;

const filterCountStyle = css`
  color: ${({ theme }) => theme.colors.textDefault};
`;

const closeButtonStyle = css`
  position: unset;
  display: flex;
  margin-left: auto;
  color: ${({ theme }) => theme.colors.closeButton};
  width: unset;
  padding: 24px 24px 0 0;
  flex-grow: 0;

  span {
    svg {
      width: 15px;
      height: 15px;
    }
    span {
      display: none;
    }
  }
`;

const BrowserHeader = styled(BaseProductBrowser.Header)`
  ${desktop`
    position: relative;
    display: flex;
    padding: 20px 32px 0 32px;
    margin-top: 32px;
    margin-bottom: 24px;
    justify-content: space-between;
    align-items: center;
  `}
`;

const GridHeaderWrapper = styled.div`
  width: 100%;
`;

const GridHeader = styled.h1`
  ${({ theme }) => theme.gridHeaderStyle};
`;

const SortDropdown = styled(BaseSortDropdown)`
  ${mobile`
    margin-right: auto;
  `}
`;

const Overlay = styled.div`
  ${mobile`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: ${overlayColor};
    z-index: 1;
  `}
`;

const NoProductsMessage = styled(EmptyGridMessage)`
  display: flex;
  justify-content: center;
  padding: 186px 0;
`;

const NoBreadcrumbsPlaceholder = styled.div`
  flex: 0 0 268px;
  margin-right: 32px;
  ${mobile`
    display: none;
  `}
`;

const CarouselHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CarouselLabel = styled.label`
  ${({ theme }) => theme.gridStyles.gridCarouselLabelStyle}
`;

const CarouselNavigationContainer = styled.div`
  display: flex;
  align-items: center;
`;

const VerticalSpacer = styled.div`
  margin-right: ${({ theme: { sizes } }) => sizes.md}px;
  ${mobile`
    margin-right: 10px;
  `}
`;

const CarouselViewAllBtn = styled(Button)`
  ${({ theme }) => theme.gridStyles.gridCarouselViewAllCta}
`;

function LoadingGridsSpinner() {
  return (
    <LoadingStatus.LoadingIndicator>
      <LoadingStatusContainer data-autotag="prodgrid-loading-symbol">
        <LoadingIconWrapper>
          <LoadingSpinnerIcon />
          <DynamicFormattedMessage
            id="global_cta.loader_default_text"
            defaultMessage="Loading Products"
          />
        </LoadingIconWrapper>
      </LoadingStatusContainer>
    </LoadingStatus.LoadingIndicator>
  );
}

const EMPTY_ARRAY = [];

const showMobileInGridAssets = config.get(
  'public.brand.features.showMobileInGridAssets'
);

const DropdownOverlay = () => {
  const { overlayVisible } = useProductBrowser();

  return overlayVisible ? <Overlay /> : null;
};

export const individualSortOptionPropType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string.isRequired,
  isDefault: PropTypes.bool,
  sort: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string,
        direction: PropTypes.oneOf(['asc', 'desc']),
      })
    ),
  ]),
});

const Header = ({
  shouldUseSharedFilters = false,
  disabledFilters = false,
  gridCTA = {},
  label,
  labelStyle,
  gender = null,
  isCarouselGrid = false,
  hasStyleGuide,
  hideSortOption = false,
  hideSideBar,
  isTabGrid,
  ...rest
}) => {
  const { isMobile } = useBreakpoint();
  const { sortOptions, filters } = useProductBrowser();
  const gridContext = useGridScrollContext();
  const desktopRef = gridContext?.desktopRef || null;

  const hasSortDropdown = useMemo(() => {
    return (
      sortOptions &&
      sortOptions.length > 0 &&
      !shouldUseSharedFilters &&
      !disabledFilters
    );
  }, [sortOptions, shouldUseSharedFilters, disabledFilters]);

  const showSortDropdown = useMemo(() => {
    return hasSortDropdown && !hideSortOption && !isCarouselGrid;
  }, [hasSortDropdown, hideSortOption, isCarouselGrid]);

  const hasGridCTA = isMobile ? gridCTA.mobile : gridCTA.desktop;

  const showFilters =
    !disabledFilters &&
    !shouldUseSharedFilters &&
    filters?.aggregations?.length > 0;

  // We only want to show the header if we have: a header, a sort dropdown, filters, or a carousel Grid. Otherwise do not show the BrowserHeader container at all
  const shouldShowHeader =
    (!isMobile && !isCarouselGrid && label && typeof label === 'string') || // if we have a non-empty header label, then we need the header
    showSortDropdown || // If we have a sort dropdown, then we need the header
    (isMobile && showFilters) || // If we have a filters, on mobile they are shown in the header
    (isCarouselGrid && !isMobile); // check for carouselGrid (this will display the lef/right arrows for the carousel), then we need the header. However, if we are mobile, the title is handled in <GridNode />

  const shouldShowSidebar = !hideSideBar && !isCarouselGrid;

  // If we do not have anything to show in the header, return nothing
  if (!shouldShowHeader) {
    return null;
  }

  return (
    <BrowserHeader $isCarouselGrid={isCarouselGrid} ref={desktopRef} {...rest}>
      {/* Breadcrumbs are no longer supported on non-Constructor grids beyond 2025 peak season. We have filters (size, color, etc.)
      and a header label but no category nav breadcrumbs (e.g. womens/tops) then we need a buffer space for the header label */}
      {shouldShowSidebar && label && <NoBreadcrumbsPlaceholder />}

      {!isMobile && !isCarouselGrid && typeof label === 'string' && label && (
        <GridHeaderWrapper>
          <GridHeader>{label}</GridHeader>
        </GridHeaderWrapper>
      )}

      {showSortDropdown && <SortDropdown />}

      {showFilters && (
        <>
          {!showSortDropdown && <MobileFiltersOnlySortPlaceholder />}
          {hasStyleGuide ? (
            <FloatingFilterBarContext>
              <FloatingFilterBar.FilterBarObserver>
                <FilterDrawerButton
                  filterCountStyle={filterCountStyle}
                  labelStyle={labelStyle}
                />
              </FloatingFilterBar.FilterBarObserver>
              {isMobile && (
                <HeaderAttachment>
                  {hasStyleGuide && <FloatingFilterBar label={label} />}
                </HeaderAttachment>
              )}
            </FloatingFilterBarContext>
          ) : (
            <FloatingFilterButton.Provider>
              <FloatingFilterButton.InlineButtonObserver>
                <FilterDrawerButton
                  filterCountStyle={filterCountStyle}
                  labelStyle={labelStyle}
                />
              </FloatingFilterButton.InlineButtonObserver>
              {isMobile ? (
                <HeaderAttachment>
                  <FloatingFilterButton icon={null} />
                </HeaderAttachment>
              ) : null}
            </FloatingFilterButton.Provider>
          )}
        </>
      )}

      {isCarouselGrid ? (
        <CarouselHeader>
          {!isMobile ? (
            <>
              <CarouselLabel style={labelStyle}>{label}</CarouselLabel>
              <CarouselNavigationContainer>
                {hasGridCTA && (
                  <>
                    <CarouselViewAllBtn variant="border" href={gridCTA.url}>
                      {gridCTA.label ? gridCTA.label : 'View All'}
                    </CarouselViewAllBtn>
                  </>
                )}
                <VerticalSpacer />
                <CarouselNavigation />
              </CarouselNavigationContainer>
            </>
          ) : null}
        </CarouselHeader>
      ) : null}
    </BrowserHeader>
  );
};

Header.propTypes = {
  disabledFilters: PropTypes.bool,
  gender: PropTypes.string,
  /**
   * Grid CTA data for carousel CTA buttons in the header.
   */
  gridCTA: PropTypes.object,
  /**
   * Indicates whether the StyleGuide is rendered on the current grid.
   */
  hasStyleGuide: PropTypes.bool,
  /**
   * Indicates whether the page has a side bar or not, which is used to determine the position of the header text.
   */
  hideSideBar: PropTypes.bool,
  /**
   * Indicates whether the sort options should show on the current grid.
   */
  hideSortOption: PropTypes.bool,
  /**
   * Indicator whether the grid is a carousel grid.
   */
  isCarouselGrid: PropTypes.bool,
  /**
   * Indicator whether this grid is inside a tab.
   */
  isTabGrid: PropTypes.bool,
  /**
   * grid header input from builder data.
   */
  label: PropTypes.string,
  labelStyle: PropTypes.object,
  shouldUseSharedFilters: PropTypes.bool,
};

const ProductSizeFilter = (props) => {
  const { isMobile } = useBreakpoint();

  return (
    <BaseProductSizeFilter
      as={isMobile ? MobileProductListingFilter : DesktopProductListingFilter}
      filterStyle={isMobile ? ProductListingFilterStyle.GRID : undefined}
      withNestedBraFilter={false}
      {...props}
    />
  );
};

const getChangedFilters = (filters, filtersToCompare) => {
  const {
    aggregationFilter: aggregationsToCompare,
    avgReviewMin: avgReviewToCompare,
  } = filtersToCompare;

  const { aggregationFilter, avgReviewMin } = filters;

  const hasNewAggregations = aggregationsToCompare
    ? Object.entries(aggregationsToCompare).some(
        ([key, value]) =>
          JSON.stringify(value) !== JSON.stringify(aggregationFilter[key])
      )
    : false;

  let newFilters = filters;

  if (hasNewAggregations) {
    newFilters = {
      ...newFilters,
      aggregationFilter: {
        ...aggregationFilter,
        ...aggregationsToCompare,
      },
    };
  }

  if (avgReviewMin !== avgReviewToCompare) {
    newFilters = {
      ...newFilters,
      avgReviewMin: avgReviewToCompare,
    };
  }

  return newFilters;
};

const ProductFilters = (props) => {
  const browserFilterState = useProductBrowserFilterState();
  const { filters } = useProductBrowser();

  // If browserFilterState is present, write our filters to the
  // browserFilterState.
  useEffect(() => {
    if (browserFilterState) {
      const setSharedFilters = browserFilterState.setFilters;
      const sharedFilters = browserFilterState.filters;

      if (!sharedFilters) {
        setSharedFilters({
          aggregationFilter: filters.aggregationFilter,
          avgReviewMin: filters.avgReviewMin,
        });
      } else {
        const newFilters = getChangedFilters(sharedFilters, filters);

        if (newFilters !== sharedFilters) {
          setSharedFilters(newFilters);
        }
      }
    }
  }, [browserFilterState, filters]);

  return <BaseProductBrowser.ProductFilters {...props} />;
};

ProductFilters.propTypes = {
  preserveFilters: PropTypes.bool,
};

const SideBar = ({
  shouldUseSharedFilters = false,
  filterTagProps,
  ...rest
}) => {
  const { enabled: isColorWheelGridFilterEnabled } = useHasFeature(
    featureNames.COLOR_WHEEL_GRID_FILTER
  );

  return (
    <ProductBrowserSideBar {...rest}>
      <FilterTags as={BaseProductBrowser.FilterTags} />
      {!shouldUseSharedFilters && (
        <ProductFilters
          listingFilterComponent={DesktopProductListingFilter}
          sizeFilterComponent={ProductSizeFilter}
          colorFilterComponent={
            isColorWheelGridFilterEnabled
              ? ColorWheelFilter
              : DesktopProductListingFilter
          }
        />
      )}
    </ProductBrowserSideBar>
  );
};

SideBar.propTypes = {
  filterTagProps: PropTypes.object,
  shouldUseSharedFilters: PropTypes.bool,
};

const FilterDrawer = ({ filterSettings, filterTagProps, ...rest }) => {
  const { enabled: isColorWheelGridFilterEnabled } = useHasFeature(
    featureNames.COLOR_WHEEL_GRID_FILTER
  );

  return (
    <BaseFilterDrawer
      dialogStyle={filterDrawerDialogStyle}
      overlayColor={overlayColor}
      closeButtonStyle={closeButtonStyle}
      closeIcon={<CloseIcon />}
      {...rest}
    >
      <BaseFilterDrawer.Content>
        <FilterTags
          as={BaseFilterDrawer.FilterTags}
          wrapperStyle={filterTagWrapperStyle}
          noFiltersAppliedStyle={noFiltersAppliedStyle}
          {...filterTagProps}
        />
        <BaseFilterDrawer.Filters>
          <BaseProductBrowser.ProductFilters
            filterSettings={filterSettings}
            listingFilterComponent={MobileProductListingFilter}
            sizeFilterComponent={ProductSizeFilter}
            colorFilterComponent={
              isColorWheelGridFilterEnabled
                ? ColorWheelFilter
                : MobileProductListingFilter
            }
          />
        </BaseFilterDrawer.Filters>
        <BaseFilterDrawer.ApplyFilters buttonStyle={applyFiltersButtonStyle} />
      </BaseFilterDrawer.Content>
    </BaseFilterDrawer>
  );
};

FilterDrawer.propTypes = {
  filterSettings: PropTypes.arrayOf(PropTypes.object),
  filterTagProps: PropTypes.object,
};

function ProductGrid({
  breakpoints,
  fplId,
  gridSlotsAsset,
  imageType,
  index,
  initialProducts,
  initialSortOption,
  isCarouselGrid = false,
  showLastChance = false,
  maxItemCount,
  preserveFilters = false,
  pdpLinkOverride,
  productSource,
  showResultsLength = false,
  showColorsLink = false,
  showGridReviews = false,
  isGqlProducts = false,
  isCioSearch = false,
  isCioBrowse = false,
  isSaleGrid = false,
  cioFilterName,
  cioFilterValue,
}) {
  const {
    filters: inputFilters,
    setFilterDrawerOpen,
    sort,
    setTotalCount,
  } = useProductBrowser();
  const { isMobile } = useBreakpoint();
  const browserFilterState = useProductBrowserFilterState();
  const actions = useProductActions();
  const apolloClient = useApolloClient();
  const { breakpoint } = useGridState();
  const isFirstRender = useRef(true);
  const initialBoostedFplId = useRef(null);
  const productImageType = useProductImageType();
  const { isLoading, startLoading } = useLoadingStatus();
  const region = useRegion();
  const { gender } = useGender();

  const { debugData: { algorithmTrace } = {} } = initialProducts;
  const { query } = useRouter();
  const cioClient = useCioClient();

  const { isVip, isElite } = useMembership();
  const secondarySite = useSecondarySite();
  const isSecondarySite = Boolean(secondarySite);
  const domain = useDomain();
  const { tld } = domain;
  const { isBuilderGridGuideEnabled } = useAdminTools();

  useReportAlgorithmTrace(algorithmTrace);

  const {
    query: { mpid },
  } = useRouter();

  const gridSlotsAssets = Array.isArray(gridSlotsAsset)
    ? gridSlotsAsset.map((asset) =>
        isMobile && asset.mobile_container?.length
          ? asset.mobile_container
          : asset.container
      )
    : [gridSlotsAsset];

  const gridAssetContainers = useAssets(gridSlotsAssets, {
    multi: true,
  });
  const { enabled: retailDiscounting } = useHasFeature(
    featureNames.RETAILPRICETEST
  );
  const filters = useDefaultProductFilters(inputFilters);
  // We want to combine XXL, XXL-1X, 1X into just XXL-1X. We have replaced the
  // XXL and 1X options to just XXL-1X, now we need to update the filters we query
  // to include the XXL and 1X options when XXL-1X is selected.
  filters.aggregationFilter = getExpandedPlusSizeFilters(
    filters.aggregationFilter
  );

  const currentSort = sort ? sort?.sort : undefined;
  const currentFilters = filters.aggregationFilter;
  const filterKeys = Object.keys(currentFilters);
  const category = filters.categoryIds.length
    ? filters.categoryIds
    : filters.backgroundCategoryIds;

  const [, setCookie, removeCookie] = useCookies(Object.keys(cookieSizeMap));
  const gridContext = useGridScrollContext();
  const handleSetScroll = gridContext?.handleSetScroll || null;

  // pdp page preselects size based on current selected filter stored as a cookie.
  const sizingCookies = useMemo(() => {
    return getSizeCookies(filters.aggregationFilter);
  }, [filters.aggregationFilter]);
  const sizingCookiesRef = useRef(sizingCookies);

  useEffect(() => {
    for (const [cookieName, cookieSize] of Object.entries(sizingCookies)) {
      if (cookieSize) {
        setCookie(cookieName, `"${cookieSize}"`);
      } else if (!cookieSize && sizingCookiesRef.current[cookieName]) {
        // This will remove cookies for values that were removed on the grid and prevents cookies from being removed
        // that were added from other sources (e.g. CF pages)
        removeCookie(cookieName);
      }
    }
    sizingCookiesRef.current = sizingCookies;
  }, [sizingCookies, setCookie, removeCookie]);

  let boostedFplId = filters.boostedFplId;
  if (initialSortOption?.sort !== sort?.sort) {
    initialBoostedFplId.current = filters.boostedFplId;
    boostedFplId = null;
  } else if (
    initialSortOption?.sort === sort?.sort &&
    initialBoostedFplId.current
  ) {
    boostedFplId = initialBoostedFplId.current;
  }

  const payload = useMemo(() => {
    const ignoreBoostedFpl =
      sort?.sort === 'fplasc' && sort?.sort === initialSortOption.sort;

    modifyFiltersForLastChance({
      filters,
      alwaysShowLastChance: showLastChance,
    });

    const payloadValue = {
      ...filters,
      sort: sort ? sort?.sort : undefined,
      boostedIds: mpid ? [parseInt(mpid)] : [],
    };

    // only apply if it's true to avoid cache miss
    if (ignoreBoostedFpl) {
      payloadValue.ignoreBoostedFpl = ignoreBoostedFpl;
    } else {
      payloadValue.boostedFplId = initialBoostedFplId.current
        ? boostedFplId
        : filters.boostedFplId;
    }
    return payloadValue;
  }, [showLastChance, boostedFplId, filters, sort, initialSortOption, mpid]);

  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);
  const [fplCode] = filters.fplCodes ?? [];

  const [state, dispatch] = useProductListingsReducer({
    key: payloadKey,
    initialProducts,
  });

  const { productPricingMap } = useInitialProductPricing({
    dispatch,
    key: payloadKey,
    initialProducts,
    useDelayedPricing: isGqlProducts || isCioSearch || isCioBrowse,
    isVip,
  });

  const { enabled: shouldUseImageService } = useHasFeature(
    featureNames.SHOULD_USE_IMAGE_SERVICE_URL
  );
  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );
  const { enabled: surfaceHiddenFacets } = useHasFeature(
    featureNames.SURFACE_HIDDEN_FACETS
  );

  const {
    productRelatedProductsMap,
    isLoading: isRelatedProductSwatchesLoading,
  } = useInitialProductRelatedProducts({
    initialProducts,
    useDelayedRelatedProducts: isGqlProducts || isCioSearch || isCioBrowse,
    shouldUseImageService:
      shouldUseImageService && (isCioSearch || isCioBrowse),
  });

  const filteredProductRelatedProductsMap =
    isCioSearch || isCioBrowse
      ? filterFutureProducts({
          products: productRelatedProductsMap,
        })
      : productRelatedProductsMap;

  // TODO: find performant way to query correct related product pricing.
  // https://techstyle-prod.atlassian.net/browse/FLD-1769
  const fallbackRelatedProductPriceMap = {};
  if (isCioSearch || isCioBrowse) {
    Object.entries(filteredProductRelatedProductsMap).forEach(
      ([key, value]) => {
        if (!value.relatedProductIdObjectList) {
          return;
        }
        value.relatedProductIdObjectList.forEach((relatedProduct) => {
          fallbackRelatedProductPriceMap[relatedProduct?.masterProductId] =
            productPricingMap[key];
        });
      }
    );
  }

  const { pages, totalCount, aggregations } = state;
  const isStale = payloadKey !== state.payloadKey;

  // purposefully excluding handleSetScroll function to avoid triggering the scroll on inital page load
  useEffect(() => {
    if (!browserFilterState) {
      if (isFirstRender.current) {
        isFirstRender.current = false;
      } else if (!isStale && handleSetScroll) {
        handleSetScroll();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStale, preserveFilters, browserFilterState]);

  const { aggregationFilter, ...currentBaseFilter } = payload;
  const currentBaseFilterKey = useMemo(
    () => JSON.stringify(currentBaseFilter),
    [currentBaseFilter]
  );
  const [baseFilterKey, setBaseFilterKey] = useState(currentBaseFilterKey);
  const { setFilterSettings } = useProductBrowser();

  useEffect(() => {
    if (
      currentBaseFilterKey === baseFilterKey ||
      isStale ||
      !aggregations ||
      isCioSearch ||
      isCioBrowse
    ) {
      return;
    }

    const filterSettings = getFilterSettings({
      aggregations,
      region,
      tld,
      gender,
      domain,
    });

    setFilterSettings(filterSettings);
    setBaseFilterKey(currentBaseFilterKey);
  }, [
    aggregations,
    baseFilterKey,
    currentBaseFilterKey,
    isStale,
    setFilterSettings,
    region,
    tld,
    gender,
    isCioSearch,
    isCioBrowse,
    domain,
  ]);

  useEffect(() => {
    setTotalCount(totalCount);
  }, [setTotalCount, totalCount]);

  const slots = useMemo(() => {
    if (!gridAssetContainers || gridAssetContainers.length < 1) {
      return [];
    }

    const mappedContainers = gridAssetContainers
      .filter(
        (gridAssetContainer) =>
          gridAssetContainer &&
          gridAssetContainer.data &&
          gridAssetContainer.data.assets
      )
      .map((gridAssetContainer) => {
        const slotAsset = Array.isArray(gridSlotsAsset)
          ? gridSlotsAsset.find((gridSlotAsset) => {
              if (isMobile && gridSlotAsset.mobile_container) {
                return (
                  gridSlotAsset.mobile_container === gridAssetContainer.name
                );
              }

              return gridSlotAsset.container === gridAssetContainer.name;
            })
          : null;
        if (slotAsset && slotAsset.position) {
          gridAssetContainer.position = slotAsset.position;
        }
        if (slotAsset && slotAsset.row) {
          gridAssetContainer.row = slotAsset.row;
        }
        if (slotAsset && slotAsset.column) {
          gridAssetContainer.column = slotAsset.column;
        }
        if (slotAsset && slotAsset.size) {
          gridAssetContainer.size = slotAsset.size;
        }

        return gridAssetContainer.data.assets.map((asset) => {
          const { customVars } = asset.options;

          return {
            asset,
            itemType: 'asset',
            position: gridAssetContainer.position
              ? gridAssetContainer.position
              : customVars.position,
            row: gridAssetContainer.row
              ? gridAssetContainer.row
              : customVars.row,
            column: gridAssetContainer.column
              ? gridAssetContainer.column
              : customVars.column,
            cellsWide:
              gridAssetContainer.size ||
              customVars.cellsWide ||
              customVars.size,
            cellsTall: customVars.cellsTall,
            minColumns: customVars.minColumns,
            maxColumns: customVars.maxColumns,
            videoAspectRatio: customVars.videoAspectRatio,
          };
        });
      });
    return [].concat(...mappedContainers);
  }, [gridAssetContainers, gridSlotsAsset, isMobile]);

  const loadItems = useCallback(
    async ({ startIndex, stopIndex }) => {
      /* Multiple re-rendering of the ProductBrowser cause lead to multiple calls to the loadItems function,
       the solution is return early if the grid is still loading.
       This removes the dupe call for most cases, but it's still present in Clear all filters click. */
      if (isLoading) {
        return;
      }
      const stopLoading = startLoading();
      const startPage = Math.floor(startIndex / pageSize) + 1;
      const stopPage = Math.floor((stopIndex - 1) / pageSize) + 1;

      for (let page = startPage; page <= stopPage; page++) {
        const hasPage = state.payloadKey === payloadKey && pages[page] != null;
        if (!hasPage) {
          let payloads = null;

          if (isCioBrowse) {
            payloads = await getCioBrowseProducts({
              page,
              pageSize,
              payload,
              cioFilterName,
              cioFilterValue,
              cioClient,
              apolloClient,
              isElite,
              isVip,
              shouldShowWarehouseProducts: isSecondarySite,
              shouldHideOriginRestricted: removeOriginFlag,
              shouldUseImageService,
              isSaleGrid,
              domain,
              surfaceHiddenFacets,
              excludeTagIds: payload.excludeTagIds,
            });
          } else if (isCioSearch) {
            payloads = await getCioSearchProducts({
              page,
              pageSize,
              payload,
              searchQuery: query.q,
              cioClient,
              apolloClient,
              isElite,
              isVip,
              shouldShowWarehouseProducts: isSecondarySite,
              shouldHideOriginRestricted: removeOriginFlag,
              shouldUseImageService,
              domain,
              excludeTagIds: payload.excludeTagIds,
            });
          } else if (isGqlProducts) {
            payloads = await getGqlProducts({
              apolloClient,
              page,
              pageSize,
              payload,
              retailDiscounting,
            });
          } else {
            payloads = await getV2Products({
              page,
              pageSize,
              payload,
              retailDiscounting,
              actions,
              productSource,
            });
          }

          try {
            dispatch({
              type: 'LOAD_PRODUCTS',
              payload: payloads,
              payloadKey,
            });
            if (isCioSearch || isCioBrowse) {
              setFilterSettings(payloads[0].aggregations);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
          } finally {
            stopLoading();
          }
        }
      }
    },
    [
      isLoading,
      startLoading,
      state.payloadKey,
      payloadKey,
      pages,
      isCioBrowse,
      isCioSearch,
      isGqlProducts,
      payload,
      cioFilterName,
      cioFilterValue,
      cioClient,
      apolloClient,
      isElite,
      isVip,
      isSecondarySite,
      removeOriginFlag,
      shouldUseImageService,
      isSaleGrid,
      domain,
      surfaceHiddenFacets,
      query.q,
      retailDiscounting,
      actions,
      productSource,
      dispatch,
      setFilterSettings,
    ]
  );

  const renderItems = useCallback(
    ({ isVisible, items, startIndex, stopIndex, lastKnownHeight }) => {
      return (
        <BaseProductGrid
          boostedFplId={boostedFplId}
          breakpoints={breakpoints}
          fplCode={fplCode}
          fplId={fplId}
          imageAspectRatio={imageAspectRatio}
          items={isVisible ? items : null}
          isCarouselGrid={isCarouselGrid}
          payloadKey={payloadKey}
          placeholderProps={{
            itemCount: stopIndex - startIndex,
            totalHeight: lastKnownHeight,
          }}
          productLinkOverride={pdpLinkOverride}
          productImageType={productImageType}
          productSource={productSource}
          startIndex={startIndex}
          showColorsLink={showColorsLink}
          showGridReviews={showGridReviews}
          isBuilderGridGuideEnabled={isBuilderGridGuideEnabled}
        />
      );
    },
    [
      boostedFplId,
      breakpoints,
      fplCode,
      fplId,
      isCarouselGrid,
      payloadKey,
      pdpLinkOverride,
      productImageType,
      productSource,
      showColorsLink,
      showGridReviews,
      isBuilderGridGuideEnabled,
    ]
  );

  const getItems = useCallback(
    ({ itemStartIndex, itemStopIndex, startIndex, stopIndex }) => {
      // TODO? Some sort of throttle applied to loadItems retries
      if (state.payloadKey !== payloadKey) {
        return null;
      }

      const startPage = Math.floor(itemStartIndex / pageSize) + 1;
      let startOffset = itemStartIndex % pageSize;
      const stopPage = Math.floor((itemStopIndex - 1) / pageSize) + 1;
      const items = [];
      for (let page = startPage; page <= stopPage; page++) {
        const pageItems = pages[page];
        if (pageItems) {
          const batchItems = startOffset
            ? pageItems.slice(startOffset)
            : pageItems;

          items.push(...batchItems);
        } else {
          // Don't have all the pages yet, bail out!
          return null;
        }
        startOffset = 0;
      }

      const length = itemStopIndex - itemStartIndex;

      // ! Careful - this can trigger an infinite loop (via loadItems) depending on
      // ! the truthiness evaluation. This should fail more gracefully.
      if (items.length < length) {
        return null; // Don't have expected number of items, return nothing!
      }
      if (items.length > length) {
        items.length = length; // Truncate.
      }

      return items;
    },
    [pages, payloadKey, state.payloadKey]
  );

  let itemCount;

  if (totalCount === 0 && isStale) {
    itemCount = null;
  } else {
    itemCount = maxItemCount ? Math.min(maxItemCount, totalCount) : totalCount;
  }

  const renderInGridAssets =
    !isMobile || (isMobile && (showMobileInGridAssets || index !== 0));

  return (
    <ProductGridPricingContext
      productGridPricing={{
        ...fallbackRelatedProductPriceMap,
        ...productPricingMap,
      }}
    >
      {showResultsLength && (
        <ResultsCountWrapper>
          {itemCount !== null && !isLoading && (
            <DynamicFormattedMessage
              id="search.results_heading_total_count"
              defaultMessage="{count} results"
              values={{
                count: itemCount,
              }}
            />
          )}
        </ResultsCountWrapper>
      )}
      {totalCount === 0 && !isStale ? (
        <NoProductsMessage onClickButton={() => setFilterDrawerOpen(true)} />
      ) : (
        <ProductGridRelatedProductsContext
          productGridRelatedProducts={filteredProductRelatedProductsMap}
          isLoading={isRelatedProductSwatchesLoading}
        >
          <VirtualScroll
            getItems={getItems}
            itemCount={itemCount}
            loadItems={loadItems}
            renderItems={renderItems}
            slots={renderInGridAssets ? slots : EMPTY_ARRAY}
            stepSize={breakpoint.columns}
            targetBatchSize={pageSize}
          />
        </ProductGridRelatedProductsContext>
      )}
      <GridFilterSegmentEvents
        filterKeys={filterKeys}
        currentFilters={currentFilters}
        currentSort={currentSort}
        category={category}
        list={productSource}
      />
    </ProductGridPricingContext>
  );
}

ProductGrid.propTypes = {
  breakpoints: BreakpointsShape,
  /* In CIO, the filter name and filter value help distinguish a specific segment of initial products to load.
   For example, `cioFilterName` can be either group ID or collection ID, while `cioFilterValue` might
   correspond to the headliner collection or the men's shorts category.
  Note: These concepts should not be confused with "filtering" results based on attributes like color or size. */
  cioFilterName: PropTypes.string,
  cioFilterValue: PropTypes.string,
  fplId: PropTypes.number,
  gridSlotsAsset: PropTypes.any,
  imageType: PropTypes.string,
  index: PropTypes.number,
  initialProducts: PropTypes.object,
  initialSortOption: individualSortOptionPropType,
  isCarouselGrid: PropTypes.bool,
  isCioBrowse: PropTypes.bool,
  isCioSearch: PropTypes.bool,
  isGqlProducts: PropTypes.bool,
  isSaleGrid: PropTypes.bool,
  /**
   * Number of products to render with VirtualScroll component.
   */
  maxItemCount: PropTypes.number,
  pdpLinkOverride: PropTypes.string,
  preserveFilters: PropTypes.bool,
  productSource: PropTypes.string,
  showColorsLink: PropTypes.bool,
  showGridReviews: PropTypes.bool,
  showLastChance: PropTypes.bool,
  showResultsLength: PropTypes.bool,
};

function Body({ gridColumnsSettings, children, className }) {
  const breakpoints = addNewGridColumnSettings(gridColumnsSettings);
  return (
    <BaseProductBrowser.Body className={className}>
      <DropdownOverlay />
      <ProductListViewedProvider>
        <GridProvider
          breakpoints={breakpoints}
          defaultBreakpoint={breakpoints[0]}
        >
          {children}
        </GridProvider>
      </ProductListViewedProvider>
    </BaseProductBrowser.Body>
  );
}

Body.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  gridColumnsSettings: gridColumnsSettingsType,
};

function SharedFilterWatcher() {
  const browserFilterState = useProductBrowserFilterState();

  const { filters, setFilters, setSort, sort } = useProductBrowser();

  const sharedFilters = browserFilterState?.filters;
  const sharedSort = browserFilterState?.sort;

  useEffect(() => {
    if (sharedFilters) {
      const newFilters = getChangedFilters(filters, sharedFilters);

      if (newFilters !== filters) {
        setFilters(newFilters);
      }
    }
  }, [sharedFilters, filters, setFilters]);

  useEffect(() => {
    if (sharedSort && sort !== sharedSort) {
      setSort(sharedSort);
    }
  }, [sharedSort, setSort, sort]);

  return null;
}

function ProductBrowser({
  aggregations = [],
  baseFilters,
  children,
  enableQueryParams,
  filterSettings: inputFilterSettings = [],
  gridSlotsAsset,
  initialFilters: initialFiltersFromProps = {},
  initialSortOption,
  shouldUseSharedFilters,
  profileSizes,
  sortOptions,
  ...rest
}) {
  const { tld } = useDomain();
  const region = useRegion();
  const { gender } = useGender();
  const domain = useDomain();
  const colorSwatches = useMemo(() => getColorSwatches(), []);

  const { enabled: isColorWheelGridFilterEnabled } = useHasFeature(
    featureNames.COLOR_WHEEL_GRID_FILTER
  );

  // Helper function to add custom_color aggregation if needed
  const addCustomColorAggregation = useCallback(
    (filters) => {
      if (!isColorWheelGridFilterEnabled || !filters?.aggregations) {
        return filters;
      }

      const hasColorAgg = filters.aggregations.some(
        (agg) => agg.field === 'color'
      );
      const hasCustomColorAgg = filters.aggregations.some(
        (agg) => agg.field === 'custom_color'
      );

      if (hasColorAgg && !hasCustomColorAgg) {
        return {
          ...filters,
          aggregations: [
            ...filters.aggregations,
            { field: 'custom_color', size: 1 },
          ],
        };
      }

      return filters;
    },
    [isColorWheelGridFilterEnabled]
  );

  // Add custom_color aggregation to baseFilters if needed
  const enhancedBaseFilters = useMemo(
    () => addCustomColorAggregation(baseFilters),
    [baseFilters, addCustomColorAggregation]
  );

  // Add custom_color aggregation to initialFilters if needed
  const enhancedInitialFilters = useMemo(
    () => addCustomColorAggregation(initialFiltersFromProps),
    [initialFiltersFromProps, addCustomColorAggregation]
  );

  let filterSettings = [];
  if (!inputFilterSettings?.length && aggregations?.length) {
    filterSettings = getFilterSettings({
      aggregations,
      region,
      tld,
      gender,
      domain,
    });
  } else {
    filterSettings = inputFilterSettings;
  }

  return (
    <BaseProductBrowser
      baseFilters={enhancedBaseFilters}
      colorSwatches={colorSwatches}
      filterSettings={filterSettings}
      initialFilters={enhancedInitialFilters}
      initialSortOption={initialSortOption}
      profileSize={profileSizes}
      sortOptions={sortOptions}
      enableQueryParams={enableQueryParams}
      {...rest}
    >
      {shouldUseSharedFilters && <SharedFilterWatcher />}
      <CategoryLabelProvider useLabels={useCategoryLabels}>
        <OptionLabelProvider useLabels={useOptionLabels}>
          {children}
        </OptionLabelProvider>
      </CategoryLabelProvider>
    </BaseProductBrowser>
  );
}

ProductBrowser.propTypes = {
  /**
   * Array of aggregations to power the filterSettings.
   * Can pass in this or filterSettings.
   */
  aggregations: PropTypes.array,
  /**
   * Temporarily passing in empty object to fix bug.
   */
  autoAppliedFilters: PropTypes.object,
  /**
   * The filter settings that cannot be unapplied; if the user resets/clears all
   * filters, this is what they will reset to.
   */
  baseFilters: PropTypes.object,
  children: PropTypes.node,
  /**
   * If true, updates query parameters every time filters are updated.
   */
  enableQueryParams: PropTypes.bool,
  filterSettings: PropTypes.arrayOf(PropTypes.object),
  /**
   * Grid CTA data from builder
   */
  gridCTA: PropTypes.object,
  /**
   * During getInitialProps routeInfo from ctx includes the inheritedRouteParamsJson
   * and routeParamsJson. This is the dev-defined json from rewrites, gridSlotsAsset
   * comes from these routeParams.
   */
  gridSlotsAsset: PropTypes.string,
  /**
   * The initial filter settings. It's possible for this to be different from
   * `baseFilters`, because other filter settings can be initialized. For instance,
   * profile applied filtering and/or url parameters.
   */
  initialFilters: PropTypes.object,
  /**
   * Initial sort to be used.
   */
  initialSortOption: individualSortOptionPropType,
  /**
   * RB key and defaultMessage based on categoryHierarchy. Should be updated to use LabelProvider
   */
  label: PropTypes.object,
  /**
   * For data analytics, primaryFplId is sent to ProductGrid. Can come from product.json
   * in rewrites. Either baseFilters.backgroundFplIds or baseFilter.fplIds
   */
  primaryFplId: PropTypes.number,
  /**
   * Provides insight for our Data Analytics Team into where a customer discovered
   *  a product. It is determined by where this <ProductGrid /> component will be
   * used. For more information about product source specifications please
   * visit: https://confluence.techstyle.net/display/EDM/Segment+-+P+Source+Specifications
   */
  productSource: PropTypes.string,
  /**
   * A user's saved sizes from their profile.
   */
  profileSizes: PropTypes.object,
  /**
   * Only has an effect if the component is wrapped by a ProductBrowserFilterContext. When false,
   * ignores changes to the ProductBrowserFilterContext. When true, consumes the value of that context
   * and uses its stored filters.
   * */
  shouldUseSharedFilters: PropTypes.bool,
  /**
   * All sort options to choose from.
   */
  sortOptions: PropTypes.arrayOf(individualSortOptionPropType),
};

export default ProductBrowser;
ProductBrowser.Body = Body;
ProductBrowser.DesktopProductListingFilter = DesktopProductListingFilter;
ProductBrowser.DropdownOverlay = DropdownOverlay;
ProductBrowser.FilterDrawer = FilterDrawer;
ProductBrowser.GridContent = GridContent;
ProductBrowser.Header = Header;
ProductBrowser.MobileProductListingFilter = MobileProductListingFilter;
ProductBrowser.LoadingGridsSpinner = LoadingGridsSpinner;
ProductBrowser.ProductFilters = ProductFilters;
ProductBrowser.ProductGrid = ProductGrid;
ProductBrowser.ProductSizeFilter = ProductSizeFilter;
ProductBrowser.SideBar = SideBar;
